import React, { useMemo, useState } from 'react';
import { fuzzySearch } from '../../../CodesThatsGottaGoSomewhereElse';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { ObjectManager } from '../../../concepts/objects';
import { Colors } from '../../../ui';
import { Badge } from '../../../ui/Badge';
import { PickerItem, PickerMenu } from '../../../ui/PickerMenu';
import { FlexScroller } from '../../../ui/Scrollers';
import { useResolve } from '../../Container';
import { Copy, Flexer, Title } from '../../dashboard/elements/LayoutElements';
import { Tag } from './Tag';
import { tagsAdd } from './TaggingCommands';

const renderTagItem = (item: TagItem, index: number) => <>
    {item.prefix && <span>{item.prefix}:</span>}
    <Badge color={item.color ?? Colors.primaryLight}>{item.text}</Badge>
</>;

export interface TagItem extends PickerItem {
    prefix?: string;
    color: string;
};

const useTagItems = () => {
    const objects = useResolve(ObjectManager);
    return useMemo(() => {
        const lookup = new Set<string>();
        const tags = Tag.sort(Array.from(objects.getAll(Tag))).filter(x => !!x.name);
        const items = [] as TagItem[];
        for (const t of tags) {
            if (lookup.has(t.name)) continue;
            lookup.add(t.name);
            items.push({ id: t.id, text: t.name, color: t.color });
        }
        return items;
    }, []);
};

const useTagCreateSuggestions = (search: string, items: TagItem[]) => {
    if (!search) return [];
    const key = search.replace(/\s+/, '').toUpperCase();
    const match = items.find(x => x.text.replace(/\s+/, '').toUpperCase() === key);
    if (!match) {
        return [{
            id: 'new',
            prefix: 'Create',
            text: `${search?.toUpperCase()}`,
        }];
    }
    return [];
};

export const InlineTagView = ({ filter, targetIds, onClose }: { filter?: string, targetIds: string[], onClose(): void }) => {

    const dispatch = useResolve(CommandDispatcher);

    const items = useTagItems();
    const [search, setSearch] = useState('');
    const suggestions = useTagCreateSuggestions(filter ?? search, items);

    const list = [
        ...fuzzySearch({ items, keys: ['text'], search: filter ?? search }),
        ...suggestions,
    ];

    const onOption = (tm: TagItem) => {
        dispatch(tagsAdd, [tm.text, '', '', tm.color, targetIds, true]);
        onClose();
    };

    return <Flexer flex="column" width="18" height="16.25">
        <Flexer padding=".5 1" background={Colors.gray50} borderRadius=".5rem .5rem 0 0" borderBottom={`1px solid ${Colors.borderLight}`}>
            <Title flush size="2xs" color={Colors.text} uppercase weight="500">Tag With...</Title>
        </Flexer>
        <FlexScroller>
            <PickerMenu
                allowKeyboard
                items={list}
                renderItem={renderTagItem}
                onAction={onOption}
            />
            {list.length === 0 &&
                <Flexer absolute inset grow flex center textAlign="center">
                    <Copy color={Colors.textLight} flush>{'Start typing to create a tag!'}</Copy>
                </Flexer>
            }
        </FlexScroller>
        <Flexer height=".5" />
    </Flexer>;
};

